/**
 * Admin API helper functions for managing backend data.
 */

const BASE_URL = '/server/JurassicServer';

// ======================== Auth ========================

export async function checkAdminRole() {
  const response = await fetch(`${BASE_URL}/admin/checkRole`);
  return response.json();
}

// ======================== Dashboard ========================

export async function getDashboardStats() {
  const response = await fetch(`${BASE_URL}/admin/stats`);
  return response.json();
}

// ======================== Questions ========================

export async function getQuestions(page = 1, limit = 20, categoryId = null) {
  let url = `${BASE_URL}/admin/questions?page=${page}&limit=${limit}`;
  if (categoryId) {
    url += `&categoryId=${categoryId}`;
  }
  const response = await fetch(url);
  return response.json();
}

export async function getQuestion(questionId) {
  const response = await fetch(`${BASE_URL}/admin/questions/${questionId}`);
  return response.json();
}

export async function createQuestion(data) {
  const response = await fetch(`${BASE_URL}/admin/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateQuestion(questionId, data) {
  const response = await fetch(`${BASE_URL}/admin/questions/${questionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function deleteQuestion(questionId) {
  const response = await fetch(`${BASE_URL}/admin/questions/${questionId}`, {
    method: 'DELETE',
  });
  return response.json();
}

export async function bulkImportQuestions(questions) {
  const response = await fetch(`${BASE_URL}/admin/questions/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questions }),
  });
  return response.json();
}

// ======================== Test Cases ========================

export async function getTestCases(page = 1, limit = 20, questionId = null) {
  let url = `${BASE_URL}/admin/testcases?page=${page}&limit=${limit}`;
  if (questionId) {
    url += `&questionId=${questionId}`;
  }
  const response = await fetch(url);
  return response.json();
}

export async function createTestCase(data) {
  const response = await fetch(`${BASE_URL}/admin/testcases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function updateTestCase(testCaseId, data) {
  const response = await fetch(`${BASE_URL}/admin/testcases/${testCaseId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function deleteTestCase(testCaseId) {
  const response = await fetch(`${BASE_URL}/admin/testcases/${testCaseId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// ======================== Users ========================

export async function getUsers(page = 1, limit = 20) {
  const response = await fetch(`${BASE_URL}/admin/users?page=${page}&limit=${limit}`);
  return response.json();
}

export async function updateUserRole(userId, role) {
  const response = await fetch(`${BASE_URL}/admin/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  return response.json();
}

export async function deleteUser(userId) {
  const response = await fetch(`${BASE_URL}/admin/users/${userId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// ======================== Submissions ========================

export async function getSubmissions(page = 1, limit = 20, userId = null, questionId = null) {
  let url = `${BASE_URL}/admin/submissions?page=${page}&limit=${limit}`;
  if (userId) url += `&userId=${userId}`;
  if (questionId) url += `&questionId=${questionId}`;
  const response = await fetch(url);
  return response.json();
}

export async function deleteSubmission(rowId) {
  const response = await fetch(`${BASE_URL}/admin/submissions/${rowId}`, {
    method: 'DELETE',
  });
  return response.json();
}

export async function resetSubmission(userId, questionId) {
  const response = await fetch(`${BASE_URL}/admin/submissions/${userId}/${questionId}/reset`, {
    method: 'POST',
  });
  return response.json();
}

// ======================== User Question Mappings ========================

export async function getMappings(page = 1, limit = 20, userId = null) {
  let url = `${BASE_URL}/admin/mappings?page=${page}&limit=${limit}`;
  if (userId) {
    url += `&userId=${userId}`;
  }
  const response = await fetch(url);
  return response.json();
}

export async function getUserMappings(userId) {
  const response = await fetch(`${BASE_URL}/admin/mappings?userId=${userId}&limit=100`);
  return response.json();
}

export async function deleteMapping(userId, questionId) {
  const response = await fetch(`${BASE_URL}/admin/mappings/${userId}/${questionId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// ======================== Categories ========================

export async function getCategories() {
  const response = await fetch(`${BASE_URL}/admin/categories`);
  return response.json();
}

export async function createCategory(categoryName) {
  const response = await fetch(`${BASE_URL}/admin/categories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ categoryName }),
  });
  return response.json();
}

export async function updateCategory(categoryId, categoryName) {
  const response = await fetch(`${BASE_URL}/admin/categories/${categoryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ categoryName }),
  });
  return response.json();
}

export async function deleteCategory(categoryId) {
  const response = await fetch(`${BASE_URL}/admin/categories/${categoryId}`, {
    method: 'DELETE',
  });
  return response.json();
}

// ======================== Dry Run ========================

export async function dryRunTests(questionId = null, categoryId = null, testCaseIds = null, questionIds = null) {
  let url = `${BASE_URL}/admin/dryrun`;
  const params = [];
  // If multiple question IDs provided, use questionIds parameter
  if (questionIds && questionIds.length > 0) {
    params.push(`questionIds=${questionIds.join(',')}`);
  } else if (questionId) {
    params.push(`questionId=${questionId}`);
  }
  if (categoryId) params.push(`categoryId=${categoryId}`);
  if (testCaseIds && testCaseIds.length > 0) {
    params.push(`testCaseIds=${testCaseIds.join(',')}`);
  }
  if (params.length > 0) {
    url += '?' + params.join('&');
  }
  const response = await fetch(url, { method: 'POST' });
  return response.json();
}

// Get test cases for a specific question
export async function getTestCasesForQuestion(questionId) {
  const response = await fetch(`${BASE_URL}/admin/testcases?questionId=${questionId}`);
  return response.json();
}

// Dry run with a custom script and/or custom test cases (not persisted)
export async function dryRunWithScript(questionId, script, testCaseIds = null, testCases = null) {
  const body = { questionId };
  if (script) {
    body.script = script;
  }
  if (testCaseIds && testCaseIds.length > 0) {
    body.testCaseIds = testCaseIds;
  }
  if (testCases && testCases.length > 0) {
    body.testCases = testCases;
  }
  const response = await fetch(`${BASE_URL}/admin/dryrun/script`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

// ======================== Leaderboard ========================

export async function getLeaderboard() {
  const response = await fetch(`${BASE_URL}/admin/leaderboard`);
  return response.json();
}

// ======================== Zoho Sheet Integration ========================

export async function fetchZohoSheetData(resourceId, accessToken) {
  const response = await fetch(`${BASE_URL}/admin/zohosheet/fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resourceId, accessToken }),
  });
  return response.json();
}

// ======================== User Timer Management ========================

export async function resetUserTimer(userId) {
  const response = await fetch(`${BASE_URL}/admin/users/${userId}/reset-timer`, {
    method: 'POST',
  });
  return response.json();
}

// ======================== Cache Management ========================

export async function reloadQuestionsCache() {
  const response = await fetch(`${BASE_URL}/admin/cache/reload`, {
    method: 'POST',
  });
  return response.json();
}

// ======================== Retry Requests ========================

export async function getRetryRequests(page = 1, limit = 20, status = 'PENDING') {
  let url = `${BASE_URL}/admin/retry-requests?page=${page}&limit=${limit}`;
  if (status) {
    url += `&status=${status}`;
  }
  const response = await fetch(url);
  return response.json();
}

export async function approveRetryRequest(rowId) {
  const response = await fetch(`${BASE_URL}/admin/retry-requests/${rowId}/approve`, {
    method: 'POST',
  });
  return response.json();
}

export async function rejectRetryRequest(rowId, reason = null) {
  const response = await fetch(`${BASE_URL}/admin/retry-requests/${rowId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return response.json();
}

export async function bulkApproveRetryRequests(rowIds) {
  const response = await fetch(`${BASE_URL}/admin/retry-requests/bulk-approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rowIds }),
  });
  return response.json();
}

export async function getRetryEmailPreview(type, userEmail, reason = null) {
  const response = await fetch(`${BASE_URL}/admin/retry-requests/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, userEmail, reason }),
  });
  return response.json();
}

// ======================== User Feedback ========================

export async function getFeedbacks(page = 1, limit = 20) {
  const response = await fetch(`${BASE_URL}/admin/feedbacks?page=${page}&limit=${limit}`);
  return response.json();
}

// ======================== Super Admin - Data Purge ========================

export async function purgeAllQuestionsAndTestCases() {
  const response = await fetch(`${BASE_URL}/admin/superadmin/purge-questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json();
}

export async function purgeAllUserData() {
  const response = await fetch(`${BASE_URL}/admin/superadmin/purge-userdata`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json();
}

export async function purgeAllData() {
  const response = await fetch(`${BASE_URL}/admin/superadmin/purge-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json();
}

// Default export as object
const adminApi = {
  checkAdminRole,
  getDashboardStats,
  getQuestions,
  getQuestion,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  bulkImportQuestions,
  getTestCases,
  createTestCase,
  updateTestCase,
  deleteTestCase,
  getUsers,
  updateUserRole,
  deleteUser,
  getSubmissions,
  deleteSubmission,
  resetSubmission,
  getMappings,
  deleteMapping,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  dryRunTests,
  dryRunWithScript,
  getLeaderboard,
  resetUserTimer,
  fetchZohoSheetData,
  reloadQuestionsCache,
  getRetryRequests,
  approveRetryRequest,
  rejectRetryRequest,
  bulkApproveRetryRequests,
  getRetryEmailPreview,
  getFeedbacks,
  purgeAllQuestionsAndTestCases,
  purgeAllUserData,
  purgeAllData,
};

export default adminApi;
